import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import {
  benefitChoice,
  config,
  credit,
  filterChoice,
  filterValues,
  InstockProvider,
  tradeIn,
  vehicle,
} from '@perxis-auto/instock-sdk/lib/app';
import { BenefitChoiceItemType } from '@perxis-auto/instock-sdk/lib/features/benefitChoice';
import { getFiltersQueryValues } from '@perxis-auto/instock-sdk/lib/features/filterChoice';
import { FilterLocationEnum } from '@perxis-auto/instock-sdk/lib/features/filterValues/types/values';
import {
  APP_EVENT_BUTTON_IDS as SDK_EVENT_BUTTON_IDS,
  APP_EVENTS as SDK_EVENTS,
} from '@perxis-auto/instock-sdk/lib/shared/config/events';
import {
  CONSTRUCTOR_FIELDS_GROUP_MAP,
  CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP,
} from '@perxis-auto/instock-sdk/lib/shared/config/fieldsGroup';
import { NavigationService } from '@perxis-auto/instock-sdk/lib/shared/services/navigation';
import { PerxisService } from '@perxis-auto/instock-sdk/lib/shared/services/perxis';
import { actions, ConfigStateType } from '@perxis-auto/instock-sdk/lib/shared/store/config';
import {
  BenefitDisclaimerEnum,
  ConstructorWidgetEnum,
  FormatterDataTypeEnum,
  LocationMarkerEnum,
  MapProviderEnum,
  MediaFormatEnum,
} from '@perxis-auto/instock-sdk/lib/shared/types';
import { EventsHandlerFunctionType } from '@perxis-auto/instock-sdk/lib/shared/types/events';
import { NavigationRouterTypeEnum } from '@perxis-auto/instock-sdk/lib/shared/types/router';
import {
  CurrencyEnum,
  CustomEventType,
  EventService,
  I18nService,
  LanguageEnum,
  NullableType,
} from '@perxis-auto/shared';
import { ExtVehicleItemType } from '@perxis-auto/shared/lib/types/schemes/vehicles';
import { PersistGate } from 'redux-persist/integration/react';

import enTranslation from '@/shared/assets/locales/en/translation.json';
import ruTranslation from '@/shared/assets/locales/ru/translation.json';
import uzTranslation from '@/shared/assets/locales/uz/translation.json';
import { APP_ANALYTIC_EVENTS, APP_EVENTS, APP_EVENTS_CHANNEL } from '@/shared/config';
import {
  DEFAULT_CONSTRUCTOR_IDS_MAP,
  DEFAULT_FILTERS_KEYS_MAP,
  GET_DEFAULT_APP_CONSTRUCTOR,
} from '@/shared/config/constructor';
import { ENV } from '@/shared/config/env';
import { PAGES } from '@/shared/config/pages';

import { App } from './App';
import { RouterProvider } from './provider';
import { setupStore } from './store';

import '@/shared/assets/styles/main.scss';

export default class InstockWidget {
  private readonly initConfig: ConfigStateType;

  private storeWithPersistor: ReturnType<typeof setupStore>;

  private readonly i18nService: I18nService<LanguageEnum>;

  private readonly navigationService: NavigationService;

  private readonly perxisService: PerxisService;

  private readonly eventService: EventService;

  private readonly eventsHandler?: EventsHandlerFunctionType;

  static Settings = {
    ConstructorType: ConstructorWidgetEnum,
    ConstructorIds: DEFAULT_CONSTRUCTOR_IDS_MAP,
    NavigationRouterType: NavigationRouterTypeEnum,
    Pages: PAGES,
    MapProvider: MapProviderEnum,
    LocationMarker: LocationMarkerEnum,
    Language: LanguageEnum,
    Currency: CurrencyEnum,
    BenefitDisclaimer: BenefitDisclaimerEnum,
    FormatterDataType: FormatterDataTypeEnum,
    MediaFormat: MediaFormatEnum,

    ConstructorFields: CONSTRUCTOR_FIELDS_GROUP_MAP,
    ConstructorReferenceFields: CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP,
  };

  static Events = {
    EventType: { ...APP_EVENTS, ...SDK_EVENTS },
    EventButtonId: SDK_EVENT_BUTTON_IDS,
  };

  constructor(initConfig: ConfigStateType, eventsHandler?: EventsHandlerFunctionType) {
    this.initConfig = initConfig;

    this.storeWithPersistor = setupStore();

    const { layout: initLayout, ...initConfigWithoutLayout } = this.initConfig;
    this.storeWithPersistor.store.dispatch(actions.update(initConfigWithoutLayout));

    const { config: configState } = this.storeWithPersistor.store.getState();
    const {
      apiKey,
      apiRoot,
      navigation,
      locale,
      language = LanguageEnum.Ru,
      currency = CurrencyEnum.RUB,
    } = configState;

    this.i18nService = I18nService.getInstance<LanguageEnum, CurrencyEnum>({
      language: language ?? LanguageEnum.Ru,
      currency: currency ?? CurrencyEnum.RUB,
      resources: {
        en: {
          translation: enTranslation,
        },
        ru: {
          translation: ruTranslation,
        },
        uz: {
          translation: uzTranslation,
        },
      },
    });

    this.storeWithPersistor.store.dispatch(
      actions.update({
        layout: GET_DEFAULT_APP_CONSTRUCTOR(configState),
      }),
    );
    this.storeWithPersistor.store.dispatch(actions.mergeLayout(initLayout));

    this.navigationService = NavigationService.getInstance({
      type: navigation?.type,
      initialEntries: navigation?.initialEntries,
    });

    this.perxisService = PerxisService.getInstance({ apiKey, apiRoot, locale });

    this.eventService = EventService.getInstance({
      eventChannel: APP_EVENTS_CHANNEL,
      analyticEvents: APP_ANALYTIC_EVENTS,
    });

    this.eventsHandler = eventsHandler;
    if (this.eventsHandler) {
      document.addEventListener(APP_EVENTS_CHANNEL, this.dispatchEvent.bind(this) as EventListener);
    }
  }

  dispatchEvent(e: CustomEventType<any>) {
    if (!this.eventsHandler) {
      return;
    }

    const { type } = e.detail;
    const eventData = e.detail;

    this.eventsHandler(type, eventData);
  }

  /**
   * Возвращает текущий объект автомобиля, если он существует.
   */
  getCurrentCar = (): NullableType<ExtVehicleItemType> => {
    const state = this.storeWithPersistor.store.getState();
    const pathname = this.navigationService.location.pathname.split('/');

    const carsPageIndex = pathname.findIndex(path => path === PAGES.Car.split('/')[1]);
    const carId = pathname[carsPageIndex + 1];

    if (carsPageIndex === -1 || !carId) {
      return null;
    }

    const params = vehicle.selectors.selectVehicleParameters(state, carId);

    const { data } = vehicle.api.endpoints.getVehicle.select(params)(state);

    if (!data) {
      return null;
    }

    return data;
  };

  /**
   * Возвращает информацию об оценке авто, полученной при использовании трейд-ин калькулятора,
   * а также для получения данных об авто, для которого был осуществлён расчёт.
   * Если пользователь не произвел расчет то, возвращается null.
   */
  getTradeInEstimate = () => {
    const state = this.storeWithPersistor.store.getState();

    const tradeInResult = tradeIn.selectors.selectSlice(state);

    return tradeInResult;
  };

  /**
   * Возвращает данные по кредитной программе и параметрам, выбранные пользователем для текущего автомобиля.
   * Если объекта автомобиля нет или пользователем не выбрана кредитная программа, возвращается null.
   */
  getSelectedCredit = () => {
    const currentCar = this.getCurrentCar();

    if (!currentCar) {
      return null;
    }

    const state = this.storeWithPersistor.store.getState();
    const { id: carId } = currentCar;

    if (!carId) {
      return null;
    }

    const creditData = credit.selectors.selectById(state.credit, carId) || {};
    const { choice = null, isSelected = false } = creditData;

    if (!isSelected || !choice?.program) {
      return null;
    }

    return { creditParams: choice, selectedProgram: choice.program };
  };

  /**
   * Возвращает информацию о текущем билде: версия, дата, git-коммит и т.п.
   */
  static getBuildInfo = () => {
    return {
      name: ENV.WIDGET_NAME ?? 'unknown',
      version: ENV.BUILD_VERSION ?? 'unknown',
      timestamp: ENV.BUILD_TIMESTAMP ?? 'unknown',
      commit: ENV.COMMIT_HASH ?? 'unknown',
      branch: ENV.BRANCH_NAME ?? 'unknown',
      env: ENV.NODE_ENV ?? 'unknown',
    };
  };

  /**
   * Возвращает массив выбранных, пользователем выгод у текущего автомобиля.
   * Если объект автомобиля или выгоды отсутствуют, возвращается null.
   */
  getSelectedBenefits = () => {
    const currentCar = this.getCurrentCar();

    if (!currentCar) {
      return null;
    }

    const state = this.storeWithPersistor.store.getState();
    const { maxBenefit, benefits } = currentCar.data;

    // Если выгоды у данного автомобиля отсутствуют
    if (!benefits || !benefits.length) {
      return null;
    }

    const selectedBenefits: BenefitChoiceItemType = benefitChoice.selectors.selectById(
      state.benefitChoice,
      currentCar.id,
    );

    // Если пользователь осуществлял выбор выгод, они хранятся в state.benefitsChoice
    if (selectedBenefits) {
      return selectedBenefits.benefits;
    }

    // Если выгоды, приводящие к максимальной скидке, у данного автомобиля отсутствуют
    if (!maxBenefit || !maxBenefit.length) {
      return [];
    }

    // Если пользователь не менял выбор выгод по умолчанию, то выбранные выгоды хранятся в maxBenefit.
    const preselectedBenefits = benefits.filter(benefit => maxBenefit.some(maxBenefit => maxBenefit.id === benefit.id));

    return preselectedBenefits;
  };

  /**
   * Устанавливает значение фильтра
   * @param key ключ фильтра
   * @param value значение
   */
  setFilter = (key: string, value: any) => {
    const state = this.storeWithPersistor.store.getState();

    const availableFilterValuesParams = filterValues.selectors.selectAvailableFilterValuesParameters(state);
    const filterGridItems = config.selectors.selectFilterGridItems(state);

    const filterGridItem = filterGridItems.find(item => item.key === key);

    const { data } = filterValues.api.endpoints.getAvailableFilterValues.select(availableFilterValuesParams)(state);
    const filterValue = filterChoice.getFilterInitialValue(key, value, filterGridItem, data);

    if (filterValue) {
      this.storeWithPersistor.store.dispatch(filterChoice.actions.setValues({ [`${key}`]: filterValue }));
    }
  };

  /**
   * Устанавливает значение фильтра по местоположению (городу или кластеру).
   */
  setLocation = (
    type: FilterLocationEnum,
    value: string,
    { filterKey } = {
      filterKey: DEFAULT_FILTERS_KEYS_MAP.CITY,
    },
  ) => {
    const state = this.storeWithPersistor.store.getState();

    const { initialValues } = config.selectors.selectFilters(state);
    const isInitialValuesEmpty = Object.keys(initialValues ?? {}).length === 0;

    const isFilterValuesSelected = filterChoice.selectors.selectIsFilterValuesSelected(state);

    const { enable: isGeolocationEnabled } = config.selectors.selectGeolocator(state);
    const availableFilterValuesItem = filterValues.selectors.selectAvailableFilterValues(state);
    const isCityFilterAvailable = !!availableFilterValuesItem.data.data[filterKey];

    if (isFilterValuesSelected || !isInitialValuesEmpty || !isCityFilterAvailable || isGeolocationEnabled) {
      return;
    }

    const filterGridItems = config.selectors.selectFilterGridItems(state);

    const filterValuesItemData = filterValues.selectors.selectAllFilterValues(state).data;

    const values = getFiltersQueryValues(
      {
        city: value,
      },
      filterGridItems,
      filterValuesItemData,
    );

    if (type === FilterLocationEnum.City) {
      this.storeWithPersistor.store.dispatch(filterChoice.actions.setValues(values));

      return;
    }

    const cityOptions = availableFilterValuesItem.data.data[filterKey] ?? [];

    // Если тип cluster — находим все города, входящие в него
    const preparedValue = cityOptions.filter(city => city.cluster?.id === values[filterKey][0]).map(city => city.id);
    if (preparedValue.length) {
      this.storeWithPersistor.store.dispatch(filterChoice.actions.setValues({ city: preparedValue }));
    }
  };

  init = async () => {
    const { config: configState } = this.storeWithPersistor.store.getState();
    const { element } = configState;

    const rootElement = document.querySelector(element);
    if (!rootElement) {
      throw new Error(`Виджет АВН. Не найден элемент ${element}`);
    }

    const root = ReactDOM.createRoot(rootElement);
    const app = (
      <I18nextProvider i18n={this.i18nService.i18n}>
        <InstockProvider store={{ store: this.storeWithPersistor.store }}>
          <PersistGate loading={null} persistor={this.storeWithPersistor.persistor}>
            <RouterProvider>
              <App />
            </RouterProvider>
          </PersistGate>
        </InstockProvider>
      </I18nextProvider>
    );

    root.render(app);
  };
}
